<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { FolderOpenIcon } from "@heroicons/vue/24/outline";
import { useJobs } from "../composables/useJobs";
import { displayStatus } from "../utils/jobDisplay";
import { isV1Job } from "../utils/inputKind";
import { useDeleteJob } from "../composables/useDeleteJob";
import { useToast } from "../composables/useToast";
import StatusBadge from "../components/StatusBadge.vue";
import EmptyState from "../components/EmptyState.vue";
import LoadingSpinner from "../components/LoadingSpinner.vue";
import JobRowMenu from "../components/JobRowMenu.vue";
import ConfirmDialog from "../components/ConfirmDialog.vue";
import UsageSidebar from "../components/UsageSidebar.vue";
import TraceReference from "../components/TraceReference.vue";

/**
 * HomeView — `/` landing page.
 *
 * Per polish design Decision #5, the full job list lives here. Each
 * row has a kebab menu (delete-job-one-pager Decision #8) — V0 has
 * one action (Delete); future wishlist items (Rename, Download, Share)
 * plug into the same JobRowMenu component.
 *
 * Row layout: the row body and the kebab are siblings (not nested),
 * so both can be real <button> elements with their own click handlers
 * — clicks on the kebab don't navigate, clicks on the body don't
 * open the menu.
 */

const router = useRouter();
const { jobs, listError, loadJobs } = useJobs();
const { deleteJob, inFlight: deleteInFlight } = useDeleteJob();
const { showSuccess, showError } = useToast();

const loaded = ref(false);
const pendingDelete = ref<{ jobId: string; jobName: string } | null>(null);

onMounted(async () => {
  await loadJobs();
  loaded.value = true;
});

function open(jobId: string) {
  router.push({ name: "job-detail", params: { id: jobId } });
}

function askDelete(jobId: string, jobName: string) {
  pendingDelete.value = { jobId, jobName };
}

async function confirmDelete() {
  if (!pendingDelete.value) return;
  const { jobId, jobName } = pendingDelete.value;
  const result = await deleteJob(jobId);
  // Close the dialog regardless of outcome — the useDeleteJob layer
  // has already updated the cached list (on success or 404) and we
  // surface success/failure via toast.
  pendingDelete.value = null;
  if (result.ok) {
    showSuccess(`Deleted "${jobName}"`);
  } else {
    // Discriminated union: `result.error: string` is guaranteed
    // when `result.ok` is false — TypeScript narrows it here.
    showError(result.error);
  }
}

function cancelDelete() {
  pendingDelete.value = null;
}

/**
 * Format the created_at timestamp as a short, locale-aware label.
 * segment_api may emit tz-naive ISO strings (Pydantic + datetime.utcnow);
 * the same `+Z` workaround used by JobDetailView applies here.
 */
function formatCreated(iso: string): string {
  const hasOffset = /Z$|[+-]\d{2}:?\d{2}$/.test(iso);
  const ms = Date.parse(hasOffset ? iso : iso + "Z");
  if (Number.isNaN(ms)) return "";
  return new Date(ms).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// Hide the broken-image icon when the thumbnail isn't on B2 (older
// completed jobs predating rally-highlights, or a worker-side
// thumbnail failure). The placeholder background of the surrounding
// <span> still occupies the grid cell so other rows don't reflow.
//
// `<img onerror>` can't distinguish 404 (expected for pre-feature
// jobs) from 401 (auth expired, unexpected) or network failure —
// they all fire the same event. Log to console so a real outage
// isn't fully invisible during debugging; the App.vue
// signed-out-event listener handles 401-driven sign-out separately
// via the actual API fetches.
function onThumbnailError(event: Event) {
  const img = event.target as HTMLImageElement | null;
  if (img) {
    console.warn("thumbnail load failed", img.src);
    img.style.display = "none";
  }
}
</script>

<template>
  <!--
    Layout: a flex container with the job-list section on the left
    and the UsageSidebar on the right. Below ~900px the sidebar drops
    above the list (responsive: `flex-col` on small, `flex-row` on
    md+). The sidebar is a sibling of the list section so it doesn't
    inherit the list's max-width constraint.
  -->
  <section
    class="p-8 max-w-6xl mx-auto flex flex-col md:flex-row md:items-start gap-6"
  >
    <div class="flex-1 min-w-0">
      <header class="mb-6 flex items-baseline justify-between">
        <h2 class="text-2xl font-semibold text-text-primary">My jobs</h2>
        <span
          v-if="jobs.length > 0"
          data-testid="home-job-count"
          class="text-sm text-text-tertiary"
        >{{ jobs.length }} {{ jobs.length === 1 ? "job" : "jobs" }}</span>
      </header>

    <div
      v-if="listError"
      data-testid="home-list-error"
      class="rounded-md border border-error-border bg-error-bg px-4 py-3 text-sm text-error"
    >
      <div>{{ listError.message }}</div>
      <TraceReference :trace-id="listError.traceId" />
    </div>

    <LoadingSpinner
      v-else-if="!loaded"
      data-testid="home-loading"
      label="Loading jobs…"
      centered
    />

    <EmptyState
      v-else-if="jobs.length === 0"
      data-testid="home-empty"
      :icon="FolderOpenIcon"
      title="No jobs yet"
      body="Upload a match video to run rally segmentation."
      :cta="{ label: '+ Upload your first video', to: 'new-job' }"
    />

    <ul
      v-else
      data-testid="home-job-list"
      class="divide-y divide-border rounded-md border border-border bg-bg-main overflow-hidden"
    >
      <li
        v-for="job in jobs"
        :key="job.job_id"
        class="flex items-center hover:bg-bg-hover transition-colors"
      >
        <button
          type="button"
          :data-testid="`job-row-${job.job_id}`"
          class="flex-1 grid grid-cols-[auto_1fr_auto_auto] items-center gap-4 px-4 py-3 text-left"
          @click="open(job.job_id)"
        >
          <!--
            Thumbnail slot. The bg colour fills the slot when there
            is no image (pre-feature jobs, jobs whose thumbnail isn't
            on B2 yet, or worker thumbnail failures) so grid columns
            stay aligned across rows. Gate is thumbnail_url PRESENCE —
            segment_api populates it the moment the worker announces
            the thumbnail artifact (customer-first delivery), so cards
            get their image while the job is still processing. The src
            stays on the proxy route (not thumbnail_url itself) so the
            existing onThumbnailError fallback covers the brief
            presign/B2 race. Absent thumbnail_url (pre-#1092 backend)
            ⇒ empty slot, exactly today's pre-completion look (C-W6).
          -->
          <span
            class="h-10 w-16 rounded bg-bg-elevated shrink-0 overflow-hidden"
            :data-testid="`job-thumbnail-${job.job_id}`"
            aria-hidden="true"
          >
            <img
              v-if="job.thumbnail_url != null"
              :src="`/api/jobs/${job.job_id}/thumbnail`"
              alt=""
              loading="lazy"
              class="h-full w-full object-cover"
              @error="onThumbnailError($event)"
            />
          </span>
          <span class="min-w-0 flex items-center gap-2">
            <span class="truncate text-sm font-medium text-text-primary">
              {{ job.job_name }}
            </span>
            <!-- RallyClip v1: submitted from a phone (input_kind ===
                 device_detections_v1). The row itself renders for
                 every status exactly like a v0 row — pending uploads
                 from another device included. -->
            <span
              v-if="isV1Job(job)"
              :data-testid="`job-input-kind-v1-${job.job_id}`"
              class="shrink-0 rounded-full border border-border bg-bg-elevated px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-text-tertiary"
            >Phone</span>
          </span>
          <!-- displayStatus: reads "Completed" from customer_done
               onward (C-W1), raw status otherwise. -->
          <StatusBadge :status="displayStatus(job)" />
          <time
            :datetime="job.created_at"
            class="text-xs text-text-tertiary tabular-nums"
          >{{ formatCreated(job.created_at) }}</time>
        </button>
        <div class="pr-3">
          <JobRowMenu
            :job-id="job.job_id"
            @delete="askDelete($event, job.job_name)"
          />
        </div>
      </li>
    </ul>

      <ConfirmDialog
        :open="pendingDelete !== null"
        :title="pendingDelete ? `Delete “${pendingDelete.jobName}”?` : ''"
        body="This permanently removes the job, the uploaded video, and the rally segmentation output. It can NOT be undone."
        primary-label="Delete forever"
        :in-flight="deleteInFlight"
        @confirm="confirmDelete"
        @cancel="cancelDelete"
      />
    </div>

    <!-- "Your work" sidebar — calendar-month aggregates for completed
         jobs. ``md:w-72`` is wide enough for "Apr: 28 videos · 5h 40m"
         without wrapping; mobile collapses above the list. -->
    <UsageSidebar class="md:w-72 md:flex-shrink-0" />
  </section>
</template>
